import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {EntityStoreUser} from 'constants/EntityStoreConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import {NavStoreRegistry, RegistryPreferences} from 'constants/RegistryConstants';
import UserConstants from 'constants/UserConstants';
import NavStore from 'nav/NavStore';
import preferences from 'Preferences';
import UpgradeAccountModal, {ENTERPRISE_FEATURES_KEYS} from 'start/upgrade-account/UpgradeAccountModal';
import licenses from 'user/Licenses';

import {navBarContentsStyles} from './NavBarContents';
import NavBarIcon, {NavBarIconProps} from './NavBarIcon';

interface RegistryIconProps {
  isActive: boolean;
  currentUser?: EntityStoreUser;
  onSectionClick: NavBarIconProps['onSectionClick'];
  section: Section;
  areSourcesLoading: boolean;
  registries: Array<NavStoreRegistry>;
}

class RegistryIcon extends React.Component<RegistryIconProps> {
  _shouldHideForAcademicUser(): boolean {
    const isAcademic =
      this.props.currentUser &&
      !licenses.hasEnterprisePlan() &&
      this.props.currentUser.proclaimed === UserConstants.PROCLAIMED_ACADEMIC;

    if (!isAcademic) {
      return false;
    }

    let hasRegistry: boolean = false;
    // In order to display the correct state when registries are still loading, use a denormalized flag that
    // we update once they have loaded. When a user first gains (or loses) access to a registry, this may
    // return incorrectly while registries are being loaded, but will update to be correct when loading is
    // complete (and on subsequent page loads).
    if (this.props.areSourcesLoading) {
      hasRegistry = preferences.get(RegistryPreferences.DENORMALIZE_ACADEMIC_HAS_REGISTRY);
    } else {
      hasRegistry = Boolean(this.props.registries && this.props.registries.length > 0);
      preferences.set(RegistryPreferences.DENORMALIZE_ACADEMIC_HAS_REGISTRY, hasRegistry);
    }

    return !(
      hasRegistry ||
      preferences.get(RegistryPreferences.REGISTRY_ACTIVATION_COMPLETE) ||
      // ENABLE_REGISTRY_ACTIVATION cannot be set through the app, but we leave it in place for ad-hoc use.
      preferences.get(RegistryPreferences.ENABLE_REGISTRY_ACTIVATION)
    );
  }

  override render(): JSX.Element {
    const sharedIconProps: NavBarIconProps = {
      icon: ModelTypeConstants.REGISTRY,
      isActive: this.props.isActive,
      section: this.props.section,
      hasActivity: false,
    };
    // NavStore fetches registries async on page load, so we check REGISTRY_ACTIVATION_COMPLETE
    // preference as well as a proxy for whether the user has registries while registries are loading.
    const hasRegistries =
      (this.props.registries && this.props.registries.length > 0) ||
      preferences.get(RegistryPreferences.REGISTRY_ACTIVATION_COMPLETE);

    return (
      <span className={css(styles.icon)}>
        {(() => {
          if (this._shouldHideForAcademicUser()) {
            return null;
          } else if (UpgradeAccountModal.isEnabled(this.props.currentUser) && !hasRegistries) {
            return (
              <NavBarIcon
                {...sharedIconProps}
                className={css(UpgradeAccountModal.hasRequested() && navBarContentsStyles.disabledIcon)}
                onSectionClick={() => {
                  UpgradeAccountModal.open(ENTERPRISE_FEATURES_KEYS.REGISTRY);
                }}
              />
            );
          }
          return <NavBarIcon {...sharedIconProps} onSectionClick={this.props.onSectionClick} />;
        })()}
      </span>
    );
  }
}

export default NavStore.connect((navStore: typeof NavStore) => ({
  registries: navStore.getRegistries(),
  areSourcesLoading: navStore.isLoading(),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}))(RegistryIcon) as any;

const styles = StyleSheet.create({
  icon: {
    position: 'relative',
  },
});
